/**
 * Контракты раздела «Настройка прав» (ФТ-6.8, E03.4).
 * Роли создаются администратором без изменения кода (ЧТЗ разд. 6, И-7).
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";
import { isPermissionCode, type PermissionCode } from "./permissions";

/** Коды ошибок управления ролями. */
export const ROLE_ERROR_CODES = {
  NOT_FOUND: "ROLE_NOT_FOUND",
  /** Код роли уже занят. */
  CODE_TAKEN: "ROLE_CODE_TAKEN",
  /** Роль назначена сотрудникам — сначала снимите назначения. */
  IN_USE: "ROLE_IN_USE",
  /** Действие оставило бы систему без роли, управляющей правами. */
  LAST_MANAGER: "ROLE_LAST_PERMISSIONS_MANAGER",
} as const;

export type RoleErrorCode = (typeof ROLE_ERROR_CODES)[keyof typeof ROLE_ERROR_CODES];

/** Код разрешения из каталога `@potok/shared`. */
export const permissionCodeSchema = z.custom<PermissionCode>(
  (value) => typeof value === "string" && isPermissionCode(value),
  { message: "Неизвестный код разрешения" },
);

/** Набор разрешений роли: без повторов. */
export const permissionSetSchema = z
  .array(permissionCodeSchema)
  .max(64)
  .transform((codes) => [...new Set(codes)]);

/**
 * Машинный код роли: латиница, цифры и подчёркивание.
 * Используется в конфигурации и журналах, поэтому фиксирован по алфавиту.
 */
export const roleCodeSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, "Код роли: латиница в нижнем регистре, цифры и подчёркивание");

/** Роль в матрице прав. */
export const roleSchema = z.object({
  id: z.string().uuid(),
  code: roleCodeSchema,
  name: z.string().min(1).max(255),
  permissions: z.array(z.string()),
  /** Сколько сотрудников назначено на роль — видно, что удаление затронет людей. */
  staffCount: z.number().int().nonnegative(),
});

export type Role = z.infer<typeof roleSchema>;

/** Матрица прав: каталог разрешений и роли. */
export const permissionsMatrixSchema = z.object({
  permissions: z.array(
    z.object({
      code: permissionCodeSchema,
      title: z.string(),
      section: z.string(),
    }),
  ),
  roles: z.array(roleSchema),
});

export type PermissionsMatrix = z.infer<typeof permissionsMatrixSchema>;

/** Конверт `GET /api/v1/admin/permissions`. */
export const permissionsMatrixResponseSchema = apiSuccessSchema(permissionsMatrixSchema);

/** Тело `PUT /api/v1/admin/permissions` — точечные правки матрицы. */
export const updateMatrixRequestSchema = z.object({
  roles: z
    .array(
      z.object({
        roleId: z.string().uuid(),
        permissions: permissionSetSchema,
      }),
    )
    .min(1),
});

export type UpdateMatrixRequest = z.infer<typeof updateMatrixRequestSchema>;

/** Тело `POST /api/v1/admin/roles`. */
export const createRoleRequestSchema = z.object({
  code: roleCodeSchema,
  name: z.string().trim().min(1).max(255),
  permissions: permissionSetSchema.default([]),
});

export type CreateRoleRequest = z.infer<typeof createRoleRequestSchema>;

/** Тело `PATCH /api/v1/admin/roles/{id}`: имя и/или набор разрешений. */
export const updateRoleRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    permissions: permissionSetSchema.optional(),
  })
  .refine((body) => body.name !== undefined || body.permissions !== undefined, {
    message: "Нужно передать имя или набор разрешений",
  });

export type UpdateRoleRequest = z.infer<typeof updateRoleRequestSchema>;

/** Конверт ответа с одной ролью. */
export const roleResponseSchema = apiSuccessSchema(roleSchema);

/** Полезная нагрузка удаления роли. */
export const deleteRoleResultSchema = z.object({ deleted: z.boolean() });

export type DeleteRoleResult = z.infer<typeof deleteRoleResultSchema>;
